/**
 * SearchPanel — Geocoding and feature search with map fly-to + add point
 */
import React, { useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { tokens } from '../../../theme';
import { useGeoveraTheme } from '../../../theme/ThemeContext';
import SearchBox from '../../SearchBox';
import { mapNavigator } from '../../../services/MapNavigator';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setSelection } from '../../../store/slices/selectionSlice';
import {
  addLayer,
  setActiveLayer,
  addFeatureAsync,
  updateLayer,
} from '../../../store/slices/layersSlice';
import { addNotification } from '../../../store/slices/uiSlice';
import type { Layer } from '../../../store/slices/layersSlice';

const SearchPanel: React.FC = () => {
  const { palette } = useGeoveraTheme();
  const dispatch = useAppDispatch();
  const layers = useAppSelector((s) => s.layers.layers);
  const activeLayerId = useAppSelector((s) => s.layers.activeLayerId);

  const flyTo = useCallback((lon: number, lat: number, zoom = 16) => {
    mapNavigator.zoomToLonLat(lon, lat, zoom);
  }, []);

  const handleZoomToFeature = useCallback(
    (featureId: string, layerId: string, geometry?: GeoJSON.Geometry) => {
      const layer = layers.find((l) => l.id === layerId);
      const feature = layer?.features?.find((f) => f.id === featureId);
      const props = feature?.properties || {};
      dispatch(
        setSelection([
          {
            layerId,
            featureId,
            properties: props,
          },
        ])
      );

      const geom = geometry || feature?.geometry;
      if (geom?.type === 'Point' && Array.isArray(geom.coordinates)) {
        const [lon, lat] = geom.coordinates as [number, number];
        flyTo(lon, lat, 17);
      } else {
        mapNavigator.zoomToSelection([{ layerId, featureId, properties: props }], layers);
      }
    },
    [dispatch, layers, flyTo]
  );

  const handleAddToLayer = useCallback(
    (coords: [number, number], address?: string) => {
      const [lon, lat] = coords;
      if (typeof lon !== 'number' || typeof lat !== 'number') return;

      const active = layers.find((l) => l.id === activeLayerId);
      const canAppend =
        active &&
        active.type === 'vector' &&
        (active.geometryType === 'Point' || !active.geometryType);

      if (canAppend && active) {
        const featureId = `${active.id}-search-${Date.now()}`;
        const feature = {
          id: featureId,
          layerId: active.id,
          geometry: { type: 'Point' as const, coordinates: [lon, lat] },
          properties: {
            name: address || 'Search point',
            source: 'search',
          },
        };
        const nextFeatures = [...(active.features || []), feature];
        dispatch(
          updateLayer({
            id: active.id,
            updates: {
              features: nextFeatures,
              featureCount: nextFeatures.length,
            },
          })
        );
        // Best-effort server
        void dispatch(
          addFeatureAsync({
            layerId: active.id,
            geometry: feature.geometry,
            properties: feature.properties,
          })
        ).catch(() => undefined);

        flyTo(lon, lat, 16);
        dispatch(
          addNotification({
            type: 'success',
            message: `Point added to “${active.name}”`,
          })
        );
        return;
      }

      // New point layer
      const id = `search-pt-${Date.now()}`;
      const layer: Layer = {
        id,
        name: address ? `Search: ${address.slice(0, 40)}` : 'Search point',
        type: 'vector',
        geometryType: 'Point',
        visible: true,
        opacity: 1,
        zIndex: layers.length + 1,
        featureCount: 1,
        features: [
          {
            id: `${id}-f0`,
            layerId: id,
            geometry: { type: 'Point', coordinates: [lon, lat] },
            properties: { name: address || 'Search point', source: 'search' },
          },
        ],
      };
      dispatch(addLayer(layer));
      dispatch(setActiveLayer(id));
      flyTo(lon, lat, 16);
      dispatch(
        addNotification({
          type: 'success',
          message: 'Search location added as a new layer',
        })
      );
    },
    [activeLayerId, dispatch, flyTo, layers]
  );

  return (
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography
        sx={{
          fontSize: tokens.fontSize.xs,
          color: palette.text.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          fontWeight: 600,
        }}
      >
        Search & geocode
      </Typography>
      <Typography sx={{ fontSize: tokens.fontSize.sm, color: palette.text.secondary }}>
        Search places or coordinates. Use “Add to layer” to drop a point on the map.
      </Typography>
      <SearchBox
        onZoomToLocation={(coords, zoom) => {
          const [lon, lat] = coords;
          if (typeof lon === 'number' && typeof lat === 'number') {
            flyTo(lon, lat, zoom || 16);
          }
        }}
        onZoomToFeature={(featureId, layerId, geometry) => {
          handleZoomToFeature(featureId, layerId, geometry);
        }}
        onPlaceMarker={(coords) => {
          if (!coords) return;
          const [lon, lat] = coords;
          if (typeof lon !== 'number' || typeof lat !== 'number') return;
          flyTo(lon, lat, 16);
          dispatch(
            addNotification({
              type: 'info',
              message: `Marker: ${lat.toFixed(5)}, ${lon.toFixed(5)}`,
            })
          );
        }}
        onAddToLayer={handleAddToLayer}
      />
    </Box>
  );
};

export default SearchPanel;
